import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Minimal markdown renderer: paragraphs, # h1, ## h2, ### h3, **bold**, *italic*, [text](url), ``` code ```, lists.
 * Kept tiny to avoid pulling in a dependency.
 */
function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split('\n');
    const blocks = [];
    let para = [];
    let listBuffer = [];
    let inCode = false;
    let codeBuffer = [];

    const flushPara = () => {
        if (para.length) {
            blocks.push({ type: 'p', text: para.join(' ') });
            para = [];
        }
    };
    const flushList = () => {
        if (listBuffer.length) {
            blocks.push({ type: 'ul', items: listBuffer });
            listBuffer = [];
        }
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        if (line.startsWith('```')) {
            flushPara(); flushList();
            if (inCode) {
                blocks.push({ type: 'code', text: codeBuffer.join('\n') });
                codeBuffer = [];
                inCode = false;
            } else {
                inCode = true;
            }
            continue;
        }
        if (inCode) { codeBuffer.push(line); continue; }
        if (!line.trim()) { flushPara(); flushList(); continue; }
        if (line.startsWith('### ')) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.slice(4) }); continue; }
        if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(3) }); continue; }
        if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h1', text: line.slice(2) }); continue; }
        if (line.startsWith('- ') || line.startsWith('* ')) { flushPara(); listBuffer.push(line.slice(2)); continue; }
        para.push(line);
    }
    flushPara(); flushList();
    if (inCode && codeBuffer.length) blocks.push({ type: 'code', text: codeBuffer.join('\n') });

    const inline = (text) => {
        // [text](url)
        let html = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-[#5B2D8E]">$1</a>');
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/(^|\s)\*([^*]+)\*/g, '$1<em>$2</em>');
        return html;
    };

    return blocks.map((b, i) => {
        switch (b.type) {
            case 'h1': return <h1 key={i} className="font-serif text-4xl md:text-5xl text-[#1A1A2E] mt-12 mb-6">{b.text}</h1>;
            case 'h2': return <h2 key={i} className="font-serif text-3xl text-[#1A1A2E] mt-10 mb-4">{b.text}</h2>;
            case 'h3': return <h3 key={i} className="font-serif text-2xl text-[#1A1A2E] mt-8 mb-3">{b.text}</h3>;
            case 'ul': return <ul key={i} className="list-disc list-outside pl-6 my-4 space-y-2 text-[#1A1A2E]">{b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}</ul>;
            case 'code': return <pre key={i} className="bg-[#1A1A2E] text-[#F9F6F0] p-4 rounded-sm overflow-x-auto text-sm my-4"><code>{b.text}</code></pre>;
            default: return <p key={i} className="font-sans text-base text-[#1A1A2E] leading-relaxed my-4" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
        }
    });
}

export default function CustomPageView() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        axios.get(`${API_URL}/api/pages/${slug}`)
            .then(res => setPage(res.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />
            <main className="pt-32 pb-24" data-testid="custom-page">
                <div className="max-w-3xl mx-auto px-6 sm:px-12">
                    {loading && (
                        <div className="text-center py-16">
                            <Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" />
                        </div>
                    )}
                    {notFound && (
                        <div className="text-center py-16">
                            <p className="overline mb-3">404</p>
                            <h1 className="font-serif text-4xl text-[#1A1A2E]">Page Not Found</h1>
                            <p className="text-[#4A4A68] mt-4">This page may have been moved or unpublished.</p>
                        </div>
                    )}
                    {page && (
                        <article data-testid="page-content">
                            <p className="overline mb-3">Page</p>
                            <h1 className="font-serif text-5xl text-[#1A1A2E] mb-2">{page.title}</h1>
                            <div className="prose prose-lg max-w-none mt-8 tiptap-view">
                                {page.content && page.content.trim().startsWith('<')
                                    ? <div dangerouslySetInnerHTML={{ __html: page.content }} />
                                    : renderMarkdown(page.content)}
                            </div>
                        </article>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
